import { RevealOnView } from '@/components/ui';
import { CONTENT_SLIDE, HEADER_FADE } from '@/utils/animation';

interface SectionHeaderProps {
  /** Heading id — used for `aria-labelledby` on the parent <section>. */
  id: string;
  title: string;
  intro: string;
  /**
   * Width pattern at md/lg breakpoints (xl is fixed 440 + 600 in all three).
   *
   * - `default`: legacy products header — title `md:w-60` (240) on the left,
   *   intro `flex-1` on the right. The intro shrinks as the viewport narrows.
   * - `fixed` (Our Story): right intro col stays at 600px from md+, left
   *   title col is `flex-1 max-w-[440px]`, and the gap between them is variable
   *   via `justify-between` — so as the page resizes, the spacing shrinks
   *   while the right column width stays put.
   * - `fluid` (Products): both columns shrink fluidly using the same clamp
   *   formulas as the `ProductRow` image+text columns below, so the section
   *   header's right column stays left-aligned with the right column of the
   *   product rows under it.
   */
  layout?: 'default' | 'fixed' | 'fluid';
  /**
   * When provided, drives the header fade + intro content slide reveal off a
   * parent's `useInView` boolean. Title h2 fades 0 → 1 over `HEADER_FADE`
   * timing; intro `<p>` content-slides up after `HEADER_FADE.durationMs +
   * HEADER_FADE.pauseMs`. When `undefined` (default) the header renders
   * statically with no animation.
   */
  visible?: boolean;
}

// Descriptor begins 25% into the header fade — the body copy starts sliding
// up while the title is still fading in, creating a tight overlap rather
// than a strict sequence.
const INTRO_DELAY_MS = Math.round(HEADER_FADE.durationMs * 0.25);

const headerFadeStyle = (visible: boolean) => ({
  opacity: visible ? 1 : 0,
  transition: `opacity ${HEADER_FADE.durationMs}ms ${HEADER_FADE.ease}`,
});

/**
 * Standard product-page section header: title-left + intro-right.
 *
 * Figma (node 47:1986, large 1440px):
 *   - Outer: `flex items-start justify-between` inside the 1200 row
 *   - Title: Karla Regular 32/32, tracking -0.025em, color #3E3A36, w-[440px]
 *   - Intro: Karla Regular 16/44, color #3E3A36, w-[600px]
 *   - Effective gap = 1200 − 440 − 600 = 160 (from justify-between)
 *
 * The `layout` prop lets each consumer pick how the columns behave at md/lg
 * so the header aligns with the layout of whatever content sits below it.
 */
export function SectionHeader({
  id,
  title,
  intro,
  layout = 'default',
  visible,
}: SectionHeaderProps) {
  const animated = visible !== undefined;
  const isVisible = visible ?? true;
  const titleStyle = animated ? headerFadeStyle(isVisible) : undefined;

  const renderIntro = (className: string) => {
    const p = <p className={className}>{intro}</p>;
    if (!animated) return p;
    return (
      <RevealOnView
        {...CONTENT_SLIDE}
        inView={isVisible}
        delayMs={INTRO_DELAY_MS}
        className={className}
      >
        <p>{intro}</p>
      </RevealOnView>
    );
  };

  if (layout === 'fluid') {
    return (
      <header className="flex w-full flex-col gap-10 md:flex-row md:items-start md:gap-10 xl:gap-40">
        <h2
          id={id}
          style={titleStyle}
          className="text-[32px] font-normal leading-8 tracking-[-0.025em] text-clearlab-dusk md:w-[clamp(240px,calc(100%_-_640px),440px)] md:flex-none xl:w-[440px]"
        >
          {title}
        </h2>
        {renderIntro(
          'text-[16px] font-normal leading-[44px] text-clearlab-midnight md:w-[clamp(0px,calc(100%_-_280px),600px)] md:flex-none xl:w-[600px]',
        )}
      </header>
    );
  }

  if (layout === 'fixed') {
    return (
      <header className="flex w-full flex-col gap-10 md:flex-row md:items-start md:justify-between">
        <h2
          id={id}
          style={titleStyle}
          className="text-[32px] font-normal leading-8 tracking-[-0.025em] text-clearlab-dusk md:min-w-[240px] md:max-w-[440px] md:flex-1 xl:w-[440px] xl:flex-none"
        >
          {title}
        </h2>
        {renderIntro('text-[16px] font-normal leading-[44px] text-clearlab-midnight md:w-[600px]')}
      </header>
    );
  }

  return (
    <header className="flex w-full flex-col gap-10 md:flex-row md:items-start md:gap-10 xl:justify-between xl:gap-10">
      <h2
        id={id}
        style={titleStyle}
        className="text-[32px] font-normal leading-8 tracking-[-0.025em] text-clearlab-dusk md:w-60 md:flex-none xl:w-[440px]"
      >
        {title}
      </h2>
      {renderIntro(
        'max-w-[600px] text-[16px] font-normal leading-[44px] text-clearlab-midnight md:flex-1 xl:w-[600px] xl:flex-none',
      )}
    </header>
  );
}
